import React, { useState, useRef, useEffect } from 'react';
import './KnowPage.css';
import '../components/ImageSlider.css';
import NavigationButtons from '../components/NavigationButtons';
import poko_wave from '../assets/poko_waveHand.png';
import { useNavigate } from 'react-router-dom';

import Slider from 'react-slick';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

import { NextArrow, PrevArrow } from '../components/Arrows';
import HearingLossChart from '../components/HearingLossChart';

import signBasics1 from '../assets/infographic/signBasics1.jpg'
import signBasics2 from '../assets/infographic/signBasics2.jpg'
import hearingLoss from '../assets/hearingloss.png';
import buddyBasicsC from '../assets/infographic/buddyBasicsC.png';
import buddyBasicsD from '../assets/infographic/buddyBasicsD.jpg'
import AuslanMap from '../components/AuslanMap';
import assistiveTools from '../assets/infographic/assistiveTools.png';
import challenge1 from '../assets/infographic/challenge1.png';
import challenge2 from '../assets/infographic/challenge2.png';
import challenge3 from '../assets/infographic/challenge3.png';
import tip1 from '../assets/infographic/tip1.png';
import tip2 from '../assets/infographic/tip2.png';
import tip3 from '../assets/infographic/tip3.png';
import assistiveTool1 from '../assets/infographic/assistiveTool1.png';
import assistiveTool2 from '../assets/infographic/assistiveTool2.png';
import assistiveTool3 from '../assets/infographic/assistiveTool3.jpg';
import externalResource1 from '../assets/infographic/externalResource1.png';
import externalResource2 from '../assets/infographic/externalResource2.png';
import externalResource3 from '../assets/infographic/externalResource3.png';

function KnowPage() {
	const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 768);

	useEffect(() => {
		const handleResize = () => setIsSmallScreen(window.innerWidth <= 768);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const [selected, setSelected] = useState('');
	const navigate = useNavigate();
	const topRef = useRef(null);

	// Refs for scroll targets
	const scrollContainerRef = useRef(null);
	const signRef = useRef(null);
	const basicsRef = useRef(null);
	const auslanRef = useRef(null);
	const toolsRef = useRef(null);
	const challengesRef = useRef(null);
	const tipsRef = useRef(null);
	const externalRef = useRef(null);
	const signSliderRef = useRef(null);
	const basicsSliderRef = useRef(null);
	const toolsSliderRef = useRef(null);
	const challengesSliderRef = useRef(null);
	const tipsSliderRef = useRef(null);
	const externalSliderRef = useRef(null);

	// Section definitions with corresponding refs for scroll and optional slider control
	const sections = [
		{ key: 'sign', ref: signRef, sliderRef: signSliderRef },
		{ key: 'basics', ref: basicsRef, sliderRef: basicsSliderRef },
		{ key: 'auslan', ref: auslanRef },
		{ key: 'tools', ref: toolsRef, sliderRef: toolsSliderRef },
		{ key: 'challenges', ref: challengesRef, sliderRef: challengesSliderRef },
		{ key: 'tips', ref: tipsRef, sliderRef: tipsSliderRef },
		{ key: 'external', ref: externalRef, sliderRef: externalSliderRef },
	];

	// Scrolls to the target section or back to top, and updates the selected key
	const handleScroll = (ref, key) => {
		if (selected === key) {
			setSelected('');
			scrollContainerRef.current?.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		} else {
			setSelected(key);
			const targetTop = ref.current?.offsetTop;
			scrollContainerRef.current?.scrollTo({
				top: targetTop,
				behavior: 'smooth',
			});
		}
	};

	// Slider settings for sections that use carousels
	const slideSettings = {
		dots: true,
		infinite: true,
		speed: 500,
		slidesToShow: 1,
		slidesToScroll: 1,
		arrows: true,
		nextArrow: <NextArrow />,
		prevArrow: <PrevArrow />,
	};

	// Scroll listener to update the active section and keyboard navigation for sliders
	useEffect(() => {
		const onScroll = () => {
			const scrollPosition = scrollContainerRef.current.scrollTop;

			const visibleSection = sections.find(({ ref }) => {
				if (!ref.current) return false;
				const offsetTop = ref.current.offsetTop;
				const offsetHeight = ref.current.offsetHeight;
				return (
					scrollPosition >= offsetTop - 50 &&
					scrollPosition < offsetTop + offsetHeight - 50
				);
			});

			if (visibleSection && visibleSection.key !== selected) {
				setSelected(visibleSection.key);
			}
		};

		// Keyboard navigation for sliders within the currently selected section
		const onKeyDown = (e) => {
			const currentSection = sections.find((section) => section.key === selected);
			const currentSlider = currentSection?.sliderRef?.current;

			if (currentSlider) {
				if (e.key === 'ArrowRight') {
					currentSlider.slickNext();
				} else if (e.key === 'ArrowLeft') {
					currentSlider.slickPrev();
				}
			}
		};

		const container = scrollContainerRef.current;
		container?.addEventListener('scroll', onScroll);
		window.addEventListener('keydown', onKeyDown);

		return () => {
			container?.removeEventListener('scroll', onScroll);
			window.removeEventListener('keydown', onKeyDown);
		};
	}, [selected, sections]);

	const BuddyBasicsContent1 = (
		<div>
			<div className='img-column buddy-img-column'>
				<LazyLoadImage src={buddyBasicsD} alt="Buddy Basics D" effect="blur" />
				<LazyLoadImage src={buddyBasicsC} alt="Buddy Basics C" effect="blur" />
			</div>
		</div>

	);

	const BuddyBasicsContent2 = (
		<div className="hearing-loss-section">
			<h2>📉 Global Hearing Loss</h2>
			<p>Explore how hearing loss is increasing around the world.</p>
			<div className="wrapper">
				<LazyLoadImage src={hearingLoss} alt="hearing loss person" effect="blur" className='hearing-loss-person' />
				<div className="chart-wrapper">
					<HearingLossChart />
				</div>
			</div>
			<div className="hearing-info">
				<div className='content-description'>
					<h5>🧐 What’s Happening?</h5>
					<p>This chart shows how more and more people around the world are having trouble hearing as time goes on:</p>
					<ul>
						<li>📅 In <strong>2019</strong>, about <strong>430 million</strong> people had hearing loss.</li>
						<li>🔮 By <strong>2030</strong>, it could be <strong>509 million</strong>.</li>
						<li>🚀 And in <strong>2050</strong>, it might grow to <strong>711 million</strong>!</li>
					</ul>
				</div>

				<div className='content-description'>
					<h5>🔍 How Do We Know That?</h5>
					<p>Doctors and scientists use data and computers to <strong>guess what might happen</strong> in the future.</p>
					<p>It’s kind of like seeing dark clouds and thinking, “It might rain!” ☁️🌧️</p>
				</div>

				<div className='content-description'>
					<h5>💡 Why It Matters</h5>
					<p>We can help protect our hearing by:</p>
					<ul>
						<li>🎧 Turning down loud sounds</li>
						<li>🏥 Getting hearing check-ups</li>
						<li>🤟 Learning sign language to include everyone</li>
					</ul>
					<p><strong>Let’s make sure everyone can hear, speak, or sign together! 🌍</strong></p>
				</div>
			</div>
		</div>

	);

	return (
		<div className="know-page page">
			<div className="white-wrapper container">
				<div className="select-section">
					<p><strong>✨ Pick a topic to explore:</strong></p>
					<div className="option-buttons">
						<button className={selected === 'basics' ? 'active' : ''} onClick={() => handleScroll(basicsRef, 'basics')}>Buddy Basics</button>
						<button className={selected === 'sign' ? 'active' : ''} onClick={() => handleScroll(signRef, 'sign')}>Sign Basics</button>
						<button className={selected === 'auslan' ? 'active' : ''} onClick={() => handleScroll(auslanRef, 'auslan')}>Auslan Info</button>
						<button className={selected === 'challenges' ? 'active' : ''} onClick={() => handleScroll(challengesRef, 'challenges')}>Challenges Facing</button>
						<button className={selected === 'tips' ? 'active' : ''} onClick={() => handleScroll(tipsRef, 'tips')}>Understanding Tips</button>
						<button className={selected === 'tools' ? 'active' : ''} onClick={() => handleScroll(toolsRef, 'tools')}>Assistive Tools</button>
						<button className={selected === 'external' ? 'active' : ''} onClick={() => handleScroll(externalRef, 'external')}>External Resources</button>
					</div>
				</div>

				<div className="scroll-content" ref={scrollContainerRef}>
					<div className="page-title" ref={topRef}>
						<h1>💡 Sign & Deaf Info</h1>
						<h5>Learn how to be an awesome buddy!</h5>
					</div>

					<div className="speech-section">
						<div className="speech-bubble-left">
							<p><strong>Hello Mate!</strong><br />I'm here to help you learn all about signs and Deaf friends!</p>
							<p className="with-select">👈 Tap a button at the side to start your sign buddy journey!</p>
							<p className="without-select">👇 Scroll down to start your sign buddy journey!</p>
						</div>
						<img src={poko_wave} alt="Buddy waving" className="poko" />
					</div>

					<div className="content-box" ref={basicsRef}>
						<h4><strong>🧠 Buddy Basics</strong></h4>
						<div className="content">
							{isSmallScreen ? (
								<>
									{BuddyBasicsContent1}
									{BuddyBasicsContent2}
								</>
							) : (
								<Slider ref={basicsSliderRef} {...slideSettings}>
									{BuddyBasicsContent1}
									{BuddyBasicsContent2}
								</Slider>
							)}

						</div>
					</div>

					<div className="content-box" ref={signRef}>
						<h4><strong>🖐️ Sign Basics</strong></h4>
						<div className="content">
							<div className='img-column'>
								<LazyLoadImage src={signBasics1} alt="Sign basic 1" effect="blur" />
								<LazyLoadImage src={signBasics2} alt="Sign basic 2" effect="blur" />
							</div>
						</div>
					</div>

					<div className="content-box" ref={auslanRef}>
						<h4><strong>🧾 Auslan Info</strong></h4>
						<div className="content">
							<div className="auslan-section">
								<h2>Auslan Users by State and Territory</h2>
								<div className="map-container">
									<AuslanMap />
								</div>
								<div className="map-info">
									<div className="content-description">
										<h5>🗺 Who Uses Auslan in Australia?</h5>
										<p>This map shows how many people use Auslan (Australian Sign Language) in each state and territory.</p>
										<p>🖐 Auslan is Australia’s unique hand-talking language — no voice, just signs and fun!</p>
									</div>
									<div className="content-description">
										<h5>📍 What Can We See?</h5>
										<ul>
											<li>👑 <strong>Victoria</strong> has the most Auslan users — over <strong>4,000</strong> people!</li>
											<li>🌞 <strong>Queensland</strong> and <strong>New South Wales</strong> follow closely, with nearly <strong>4,000</strong> each.</li>
											<li>✨ Even in places with fewer people, like <strong>Tasmania</strong> or the <strong>Northern Territory</strong>, there are still Auslan users!</li>
										</ul>
									</div>
									<div className="content-description">
										<h5>💡 Why It’s Cool:</h5>
										<p>No matter where you live in Australia, someone nearby might use Auslan.</p>
										<p>That’s why learning even a few signs can help us say <strong>"hello"</strong> and make new friends!</p>

										<p>🤝 Let’s include everyone — with signs, smiles, and open hands!</p>
									</div>
								</div>

							</div>
						</div>
					</div>

					<div className="content-box" ref={challengesRef}>
						<h4><strong>😕 Challenges Facing</strong></h4>
						<div className="content">
							<Slider ref={challengesSliderRef} {...slideSettings}>
								<div><LazyLoadImage src={challenge1} alt="Challenge 1" effect="blur" visibleByDefault={true} /></div>
								<div><LazyLoadImage src={challenge2} alt="Challenge 2" effect="blur" visibleByDefault={true} /></div>
								<div><LazyLoadImage src={challenge3} alt="Challenge 3" effect="blur" visibleByDefault={true} /></div>
							</Slider>
						</div>
					</div>

					<div className="content-box" ref={tipsRef}>
						<h4><strong>💡 Understanding Tips</strong></h4>
						<div className="content">
							<Slider ref={tipsSliderRef} {...slideSettings}>
								<div><LazyLoadImage src={tip1} alt="Tip 1" effect="blur" visibleByDefault={true} /></div>
								<div><LazyLoadImage src={tip2} alt="Tip 2" effect="blur" visibleByDefault={true} /></div>
								<div><LazyLoadImage src={tip3} alt="Tip 3" effect="blur" visibleByDefault={true} /></div>
							</Slider>
						</div>
					</div>

					<div className="content-box" ref={toolsRef}>
						<h4><strong>🔧 Assistive Tools</strong></h4>
						<div className="content">
							<Slider ref={toolsSliderRef} {...slideSettings}>
								<div><LazyLoadImage src={assistiveTools} alt="Assistive Tools" effect="blur" visibleByDefault={true} /></div>
								<div><LazyLoadImage src={assistiveTool1} alt="Assistive Tool 1" effect="blur" visibleByDefault={true} /></div>
								<div><LazyLoadImage src={assistiveTool2} alt="Assistive Tool 2" effect="blur" visibleByDefault={true} /></div>
								<div><LazyLoadImage src={assistiveTool3} alt="Assistive Tool 3" effect="blur" visibleByDefault={true} /></div>
							</Slider>
						</div>
					</div>

					<div className="content-box external" ref={externalRef}>
						<h4><strong>🌏 External Resources</strong></h4>
						<div className="content">
							<Slider ref={externalSliderRef} {...slideSettings}>
								<div className='content-description'>
									<strong>Sally and Possum</strong>
									<p>A fun TV show where you can watch stories, learn Auslan, and see how Deaf friends talk using signs!</p>
									<div className='link-wrapper'>
										<LazyLoadImage src={externalResource1} alt="Sally and Possum" effect="blur" />
										<a href="https://www.youtube.com/playlist?list=PLqvWKFAFbNjEPOUbl4DHtYJ80iardMY4D" target="_blank" rel="noopener noreferrer">▶️ Watch on YouTube</a>
									</div>
								</div>
								<div className='content-description'>
									<strong>Auslan Signbank</strong>
									<p>A big online dictionary full of Auslan signs. You can look up words and see how to sign them!</p>
									<div className='link-wrapper'>
										<LazyLoadImage src={externalResource2} alt="Auslan Signbank" effect="blur" />
										<a href="https://auslan.org.au" target="_blank" rel="noopener noreferrer">📘 Visit Auslan Signbank</a>
									</div>
								</div>
								<div className='content-description'>
									<strong>Free Auslan Posters</strong>
									<p>You can download colorful posters here to help you learn signs for animals, feelings, and more!</p>
									<div className='link-wrapper'>
										<LazyLoadImage src={externalResource3} alt="Free Auslan Posters" effect="blur" />
										<a href="https://www.waad.org.au/home" target="_blank" rel="noopener noreferrer">🎨 Download Posters</a>
									</div>
								</div>
							</Slider>
						</div>
					</div>
				</div>
			</div>

			<NavigationButtons />
		</div>
	);

}

export default KnowPage;
